import { cn } from '@/lib/tailwind-utils';
import { ToSubOptions, useLinkProps } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';
import { ReactNode, useMemo } from 'react';
import { CopyButton } from './copy-button';

type CopyableLinkBaseProps = {
  children: ReactNode;
  whenUrlEmptyRender?: ReactNode;
  canCopyUrl?: boolean;
  className?: string;
  showExternalHrefIcon?: boolean;
  'data-testid'?: string;
};

type InternalLinkProps = CopyableLinkBaseProps & {
  to: ToSubOptions['to'];
  params?: ToSubOptions['params'];
  externalHref?: never;
};

type ExternalLinkProps = CopyableLinkBaseProps & {
  externalHref: string;
  to?: never;
  params?: never;
};

export type CopyableLinkProps = InternalLinkProps | ExternalLinkProps;

export function CopyableLink({
  children,
  to,
  params,
  externalHref,
  whenUrlEmptyRender,
  canCopyUrl = true,
  className,
  showExternalHrefIcon = true,
  'data-testid': dataTestId,
}: CopyableLinkProps) {
  const props = useLinkProps({ to, params });
  const { href, linkProps, copyableLink } = useMemo(() => {
    if (externalHref) {
      const linkProps = { rel: 'noopener noreferrer', target: '_blank' };
      return { href: externalHref, linkProps, copyableLink: externalHref };
    }
    if (to) {
      const { href, ...linkProps } = props;
      return { href, linkProps, copyableLink: new URL(href!, window.location.origin).href };
    }
    return {};
  }, [externalHref, props, to]);

  if (!href) {
    return whenUrlEmptyRender || null;
  }

  return (
    <div className="flex items-center gap-2">
      <a
        {...linkProps}
        href={href}
        className={cn(
          'flex items-center justify-start text-blue-500 underline p-0 text-md whitespace-normal break-all max-w-[300px] h-auto',
          className,
        )}
        data-testid={dataTestId}
      >
        <div className="flex items-center gap-1">
          {children}
          {showExternalHrefIcon && !!externalHref && <ExternalLink className="w-4 h-4" />}
        </div>
      </a>
      {canCopyUrl && <CopyButton content={copyableLink} />}
    </div>
  );
}
